import { Button } from "@ai-teach/ui-web";
import type React from "react";
import { useCallback, useEffect, useState } from "react";
import toast from "react-hot-toast";
import {
  LuCalendar,
  LuCopy,
  LuDownload,
  LuHardDrive,
  LuInfo,
  LuQrCode,
  LuSearch,
  LuSmartphone,
  LuTrash2,
  LuUpload,
  LuX,
} from "react-icons/lu";
import { QRCode } from "react-qrcode-logo";

interface ApkRelease {
  id: string;
  versionName: string;
  versionCode: number;
  fileName: string;
  fileSize: number;
  description?: string;
  downloadUrl: string;
  createdAt: string;
}

const getApiBaseUrl = () => {
  if (import.meta.env.VITE_API_BASE_URL) return import.meta.env.VITE_API_BASE_URL;
  const apiUrl = import.meta.env.VITE_API_URL || "http://localhost:4001/graphql";
  return apiUrl.replace(/\/graphql$/, "");
};

// The public download page is served by the web app (e.g. https://mypocketteacher.com/download),
// not the API. QR codes and shared links point here instead of the direct .apk URL so WeChat's
// in-app browser (which blocks direct .apk downloads) lands on a real page that can guide users.
const getDownloadPageUrl = () => {
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  return `${origin}/download`;
};

// Formats bytes to standard human readable string (e.g. "12.5 MB")
const formatBytes = (bytes: number, decimals = 2) => {
  if (bytes === 0) return "0 Bytes";
  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ["Bytes", "KB", "MB", "GB", "TB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${Number.parseFloat((bytes / k ** i).toFixed(dm))} ${sizes[i]}`;
};

export function ApksPage() {
  const [releases, setReleases] = useState<ApkRelease[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  // Form states
  const [versionName, setVersionName] = useState("");
  const [versionCode, setVersionCode] = useState("");
  const [description, setDescription] = useState("");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  // Search/Filter states
  const [searchTerm, setSearchTerm] = useState("");

  // Modal states for QR Code
  const [qrModalOpen, setQrModalOpen] = useState(false);
  const [activeReleaseForQr, setActiveReleaseForQr] = useState<ApkRelease | null>(null);

  const fetchReleases = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetch(`${getApiBaseUrl()}/api/apk-releases`, {
        credentials: "include",
      });

      if (!response.ok) {
        throw new Error("Failed to fetch APK releases");
      }

      const data = await response.json();
      setReleases(data);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "載入 APK 釋出版本失敗");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchReleases();
  }, [fetchReleases]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      if (!file?.name.toLowerCase().endsWith(".apk")) {
        toast.error("只能上傳 .apk 格式的檔案！");
        e.target.value = "";
        return;
      }
      setSelectedFile(file);
    }
  };

  const handleUploadSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedFile) {
      toast.error("請選擇 APK 檔案！");
      return;
    }
    if (!versionName.trim()) {
      toast.error("請輸入版本名稱！");
      return;
    }
    if (!versionCode.trim() || Number.isNaN(Number(versionCode))) {
      toast.error("請輸入有效的整數版本號！");
      return;
    }

    try {
      setUploading(true);
      setUploadProgress(10); // Start progress bar animation

      // 1. Upload through the API so the write replicates to the other S3 partition.
      // XHR rather than fetch: only XHR reports upload progress for a multi-hundred-MB body.
      const uploadUrl = `${getApiBaseUrl()}/api/apk-releases/upload?fileName=${encodeURIComponent(
        selectedFile.name
      )}`;

      const s3Key = await new Promise<string>((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open("POST", uploadUrl, true);
        xhr.withCredentials = true;
        xhr.setRequestHeader(
          "Content-Type",
          selectedFile.type || "application/vnd.android.package-archive"
        );

        xhr.upload.onprogress = (event) => {
          if (event.lengthComputable) {
            const percentComplete = Math.round((event.loaded / event.total) * 80) + 10; // Scale between 10% and 90%
            setUploadProgress(percentComplete);
          }
        };

        xhr.onload = () => {
          let payload: { s3Key?: string; error?: string } = {};
          try {
            payload = JSON.parse(xhr.responseText);
          } catch {
            // fall through to the status check below
          }

          if (xhr.status === 200 && payload.s3Key) {
            resolve(payload.s3Key);
          } else {
            reject(new Error(payload.error || "上傳 APK 失敗"));
          }
        };

        xhr.onerror = () => reject(new Error("上傳過程中發生網路錯誤"));
        xhr.send(selectedFile);
      });

      setUploadProgress(90);

      // 2. Confirm upload and save metadata to MongoDB
      const confirmRes = await fetch(`${getApiBaseUrl()}/api/apk-releases`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({
          versionName: versionName.trim(),
          versionCode: Number.parseInt(versionCode, 10),
          s3Key,
          fileName: selectedFile.name,
          fileSize: selectedFile.size,
          description: description.trim(),
        }),
      });

      if (!confirmRes.ok) {
        const confirmErr = await confirmRes.json();
        throw new Error(confirmErr.error || "確認 APK 上傳失敗");
      }

      setUploadProgress(100);
      toast.success("APK 上傳與版本建立成功！");

      // Reset form
      setVersionName("");
      setVersionCode("");
      setDescription("");
      setSelectedFile(null);
      // Reset file input element
      const fileInput = document.getElementById("apk-file-input") as HTMLInputElement;
      if (fileInput) fileInput.value = "";

      // Reload releases list
      fetchReleases();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "上傳 APK 失敗");
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm("確定要刪除此 APK 版本嗎？這將會同步自 S3 雲端與資料庫中刪除！")) {
      return;
    }

    try {
      const response = await fetch(`${getApiBaseUrl()}/api/apk-releases/${id}`, {
        method: "DELETE",
        credentials: "include",
      });

      if (!response.ok) {
        const errData = await response.json();
        throw new Error(errData.error || "刪除 APK 失敗");
      }

      toast.success("APK 版本已成功刪除");
      fetchReleases();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "刪除 APK 失敗");
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast.success("下載連結已複製到剪貼簿！");
  };

  const openQrModal = (release: ApkRelease) => {
    setActiveReleaseForQr(release);
    setQrModalOpen(true);
  };

  const filteredReleases = releases.filter((release) => {
    const term = searchTerm.toLowerCase();
    return (
      release.versionName.toLowerCase().includes(term) ||
      release.fileName.toLowerCase().includes(term) ||
      release.description?.toLowerCase().includes(term)
    );
  });

  // Build the direct APK download URL (API responds with a 302 redirect to the S3 object)
  const getDirectDownloadUrl = (id: string) => {
    return `${getApiBaseUrl()}/api/apk-releases/${id}/download`;
  };

  return (
    <div className="min-h-screen p-6 bg-slate-50 dark:bg-slate-900 transition-colors duration-300">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Page Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-slate-200 dark:border-slate-800 pb-6">
          <div>
            <h1 className="text-3xl font-extrabold tracking-tight text-slate-900 dark:text-slate-100 flex items-center gap-3">
              <span className="p-2.5 bg-indigo-500/10 text-indigo-500 rounded-xl">
                <LuSmartphone className="w-8 h-8" />
              </span>
              APK 版本管理
            </h1>
            <p className="mt-2 text-slate-600 dark:text-slate-400">
              上傳新版 Android APK，系統將自動生成專屬的下載連結與 QR Code 供快速發布下載。
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column: Upload New Release Form */}
          <div className="lg:col-span-1">
            <div className="panel p-6 bg-white dark:bg-slate-800 shadow-md border border-slate-200/60 dark:border-slate-700/50 rounded-2xl">
              <h2 className="text-xl font-bold text-slate-950 dark:text-slate-50 mb-4 flex items-center gap-2 border-b border-slate-100 dark:border-slate-700 pb-3">
                <LuUpload className="w-5 h-5 text-indigo-500" />
                發布新版本
              </h2>

              <form onSubmit={handleUploadSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-1.5">
                    版本名稱 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    required
                    placeholder="例如: 1.0.5"
                    className="w-full px-3.5 py-2 border border-slate-300 dark:border-slate-600 rounded-xl bg-slate-50 dark:bg-slate-900 text-slate-900 dark:text-slate-100 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500/30 focus:border-indigo-500 transition-all"
                    value={versionName}
                    onChange={(e) => setVersionName(e.target.value)}
                    disabled={uploading}
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-1.5">
                    整數版本號 (Version Code) <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    required
                    placeholder="例如: 5 (必須比上一版大)"
                    className="w-full px-3.5 py-2 border border-slate-300 dark:border-slate-600 rounded-xl bg-slate-50 dark:bg-slate-900 text-slate-900 dark:text-slate-100 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500/30 focus:border-indigo-500 transition-all"
                    value={versionCode}
                    onChange={(e) => setVersionCode(e.target.value)}
                    disabled={uploading}
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-1.5">
                    上傳 APK 檔案 <span className="text-red-500">*</span>
                  </label>
                  <div className="relative border-2 border-dashed border-slate-300 dark:border-slate-600 hover:border-indigo-500 dark:hover:border-indigo-400 rounded-2xl p-6 transition-colors flex flex-col items-center justify-center bg-slate-50/50 dark:bg-slate-900/30 text-center">
                    <input
                      id="apk-file-input"
                      type="file"
                      required
                      accept=".apk"
                      onChange={handleFileChange}
                      className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                      disabled={uploading}
                    />
                    <LuUpload className="w-10 h-10 text-slate-400 dark:text-slate-500 mb-3" />
                    {selectedFile ? (
                      <div className="space-y-1">
                        <p className="text-sm font-bold text-indigo-600 dark:text-indigo-400 line-clamp-1">
                          {selectedFile.name}
                        </p>
                        <p className="text-xs text-slate-500 dark:text-slate-400">
                          {formatBytes(selectedFile.size)}
                        </p>
                      </div>
                    ) : (
                      <div className="space-y-1">
                        <p className="text-sm font-medium text-slate-700 dark:text-slate-300">
                          點擊或拖曳 APK 檔案至此
                        </p>
                        <p className="text-xs text-slate-500 dark:text-slate-500">
                          僅限 .apk 安裝包
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-1.5">
                    更新日誌 / 說明 (選填)
                  </label>
                  <textarea
                    placeholder="說明此版本新增了哪些功能、修復了哪些 Bug..."
                    rows={4}
                    className="w-full px-3.5 py-2 border border-slate-300 dark:border-slate-600 rounded-xl bg-slate-50 dark:bg-slate-900 text-slate-900 dark:text-slate-100 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500/30 focus:border-indigo-500 transition-all text-sm resize-none"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    disabled={uploading}
                  />
                </div>

                {uploading && (
                  <div className="space-y-2 py-1">
                    <div className="flex justify-between text-xs font-semibold text-slate-600 dark:text-slate-400">
                      <span>檔案上傳中...</span>
                      <span>{uploadProgress}%</span>
                    </div>
                    <div className="w-full h-2 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-indigo-500 rounded-full transition-all duration-300 ease-out"
                        style={{ width: `${uploadProgress}%` }}
                      />
                    </div>
                  </div>
                )}

                <Button
                  type="submit"
                  disabled={uploading}
                  variant="primary"
                  className="w-full py-2.5 font-bold flex items-center justify-center gap-2 rounded-xl transition-all shadow-md active:scale-95"
                >
                  {uploading ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      正在處理上傳...
                    </>
                  ) : (
                    "開始上傳並發布"
                  )}
                </Button>
              </form>
            </div>
          </div>

          {/* Right Column: APK Releases List */}
          <div className="lg:col-span-2 space-y-4">
            {/* Search and Filters Bar */}
            <div className="flex items-center gap-3 px-4 py-3 bg-white dark:bg-slate-800 shadow-sm border border-slate-200/50 dark:border-slate-700/50 rounded-2xl">
              <LuSearch className="w-5 h-5 text-slate-400 shrink-0" />
              <input
                type="text"
                placeholder="搜尋版本名稱、檔案名稱或日誌說明..."
                className="w-full bg-transparent text-slate-900 dark:text-slate-100 placeholder:text-slate-400 border-none focus:outline-none focus:ring-0 text-sm"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <button
                type="button"
                onClick={() => setSearchTerm("")}
                className={`text-slate-400 hover:text-slate-600 p-0.5 rounded-full hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors ${!searchTerm ? "invisible" : ""}`}
              >
                <LuX className="w-4 h-4" />
              </button>
            </div>

            {/* Releases Panel */}
            <div className="panel bg-white dark:bg-slate-800 shadow-md border border-slate-200/60 dark:border-slate-700/50 rounded-2xl overflow-hidden">
              <div className="p-4 border-b border-slate-100 dark:border-slate-700 flex justify-between items-center bg-slate-50/50 dark:bg-slate-900/10">
                <h3 className="font-bold text-slate-900 dark:text-slate-100 flex items-center gap-2">
                  <LuHardDrive className="w-5 h-5 text-indigo-500" />
                  已上傳版本列表 ({filteredReleases.length})
                </h3>
                <button
                  type="button"
                  onClick={fetchReleases}
                  className="text-xs font-semibold text-indigo-600 dark:text-indigo-400 hover:underline"
                >
                  重新整理
                </button>
              </div>

              {loading ? (
                <div className="py-20 flex flex-col items-center justify-center text-slate-400 space-y-3">
                  <div className="w-8 h-8 border-3 border-indigo-500 border-t-transparent rounded-full animate-spin" />
                  <p className="text-sm">載入版本列表中...</p>
                </div>
              ) : filteredReleases.length === 0 ? (
                <div className="py-20 flex flex-col items-center justify-center text-slate-400 text-center px-4">
                  <LuInfo className="w-12 h-12 text-slate-300 dark:text-slate-600 mb-3" />
                  <p className="text-base font-bold text-slate-700 dark:text-slate-300">
                    目前沒有任何 APK 版本
                  </p>
                  <p className="text-xs mt-1 max-w-sm text-slate-500">
                    上傳您的第一個 APK 檔案，開始發布測試吧！
                  </p>
                </div>
              ) : (
                <div className="divide-y divide-slate-100 dark:divide-slate-700 gradient-scrollbar max-h-[640px] overflow-y-auto">
                  {filteredReleases.map((release) => {
                    const downloadUrl = getDirectDownloadUrl(release.id);
                    return (
                      <div
                        key={release.id}
                        className="p-5 hover:bg-slate-50/70 dark:hover:bg-slate-900/20 transition-colors flex flex-col md:flex-row md:items-start justify-between gap-4"
                      >
                        <div className="space-y-2 flex-1">
                          {/* Title & Badge */}
                          <div className="flex flex-wrap items-center gap-2.5">
                            <span className="text-base font-extrabold text-slate-950 dark:text-slate-55 flex items-center gap-1.5">
                              {release.versionName}
                            </span>
                            <span className="px-2 py-0.5 text-xs font-semibold bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 border border-indigo-500/20 rounded-md">
                              Code: {release.versionCode}
                            </span>
                            <span className="text-xs text-slate-500 flex items-center gap-1">
                              <LuCalendar className="w-3.5 h-3.5" />
                              {new Date(release.createdAt).toLocaleDateString()}
                            </span>
                          </div>

                          {/* File detail info */}
                          <div className="flex flex-col space-y-1 bg-slate-50 dark:bg-slate-900/50 p-2.5 rounded-xl border border-slate-100 dark:border-slate-800 text-xs">
                            <p className="text-slate-700 dark:text-slate-300 font-mono break-all font-medium">
                              檔名: {release.fileName}
                            </p>
                            <p className="text-slate-500 dark:text-slate-400">
                              大小: {formatBytes(release.fileSize)}
                            </p>
                          </div>

                          {/* Description / Changelog */}
                          {release.description ? (
                            <p className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed whitespace-pre-line bg-slate-50/40 dark:bg-slate-900/10 p-2.5 rounded-xl border border-slate-100/50 dark:border-slate-800/40">
                              {release.description}
                            </p>
                          ) : (
                            <p className="text-xs text-slate-400 dark:text-slate-500 italic">
                              無更新說明
                            </p>
                          )}
                        </div>

                        {/* Actions block */}
                        <div className="flex flex-row md:flex-col items-center gap-2 md:self-stretch justify-end">
                          <button
                            type="button"
                            onClick={() => openQrModal(release)}
                            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-indigo-600 dark:text-indigo-400 hover:bg-indigo-500/10 border border-indigo-500/20 rounded-xl transition-all"
                            title="顯示二維碼"
                          >
                            <LuQrCode className="w-4 h-4" />
                            <span>二維碼</span>
                          </button>

                          <button
                            type="button"
                            onClick={() => copyToClipboard(getDownloadPageUrl())}
                            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-xl transition-all"
                            title="複製下載頁面連結"
                          >
                            <LuCopy className="w-4 h-4" />
                            <span>複製連結</span>
                          </button>

                          <a
                            href={downloadUrl}
                            download
                            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-green-600 dark:text-green-400 hover:bg-green-500/10 border border-green-500/20 rounded-xl transition-all"
                            title="直接下載安裝檔"
                          >
                            <LuDownload className="w-4 h-4" />
                            <span>直接下載</span>
                          </a>

                          <button
                            type="button"
                            onClick={() => handleDelete(release.id)}
                            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-red-600 dark:text-red-400 hover:bg-red-500/10 border border-red-500/20 rounded-xl transition-all md:mt-auto"
                            title="刪除此版本"
                          >
                            <LuTrash2 className="w-4 h-4" />
                            <span>刪除</span>
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* QR Code Modal */}
      {qrModalOpen && activeReleaseForQr && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/60 backdrop-blur-sm animate-fade-in">
          <div className="relative w-full max-w-md bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-3xl p-6 shadow-2xl space-y-6">
            {/* Modal Header */}
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-bold text-slate-950 dark:text-slate-50 flex items-center gap-2">
                <LuQrCode className="text-indigo-500 w-5 h-5" />
                掃描二維碼下載 APK
              </h3>
              <button
                type="button"
                onClick={() => setQrModalOpen(false)}
                className="p-1.5 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 bg-slate-100 dark:bg-slate-700 rounded-full transition-colors"
              >
                <LuX className="w-4 h-4" />
              </button>
            </div>

            {/* QR Code Content */}
            <div className="flex flex-col items-center justify-center bg-slate-50 dark:bg-slate-900 p-6 rounded-2xl border border-slate-100 dark:border-slate-800">
              <QRCode
                value={getDownloadPageUrl()}
                size={200}
                logoImage="/icon/app_icon.png"
                logoWidth={50}
                ecLevel="H"
              />
              <p className="mt-4 text-xs font-bold text-indigo-600 dark:text-indigo-400">
                版本: {activeReleaseForQr.versionName} (Code: {activeReleaseForQr.versionCode})
              </p>
              <p className="mt-1 text-[11px] text-slate-500 text-center max-w-[280px]">
                請使用手機相機或二維碼掃描程式，掃描上方圖示，即可前往下載頁面下載 APK 檔案。
              </p>
            </div>

            {/* Download Page Link Copy */}
            <div className="space-y-1.5">
              <span className="text-xs font-semibold text-slate-600 dark:text-slate-400">
                下載連結:
              </span>
              <div className="flex gap-2 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl p-2 items-center text-xs">
                <span className="font-mono text-slate-600 dark:text-slate-300 break-all select-all flex-1 pl-1 line-clamp-1">
                  {getDownloadPageUrl()}
                </span>
                <button
                  type="button"
                  onClick={() => copyToClipboard(getDownloadPageUrl())}
                  className="p-1.5 bg-white dark:bg-slate-800 text-indigo-500 hover:text-indigo-600 rounded-lg shadow-sm border border-slate-200 dark:border-slate-600 transition-colors"
                  title="複製連結"
                >
                  <LuCopy className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>

            <Button
              type="button"
              variant="secondary"
              onClick={() => setQrModalOpen(false)}
              className="w-full py-2.5 font-bold rounded-xl"
            >
              關閉頁面
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
